import React, { useEffect, useRef } from "react";
import { useParams, useNavigate, Outlet } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { appApi, useGetMyProjectsQuery, useGetAllProjectsForMatrixQuery } from "../../../store/appApi";
import { setActiveProject, clearActiveProject, selectActiveProjectId } from "../../../store/projectSlice";
import { selectUserRole } from "../../../store/authSlice";

// Tags covering all project-scoped data — invalidated on every project switch
// so RTK Query re-fetches fresh data for the newly active project.
const PROJECT_SCOPED_TAGS = [
  "Dashboard",
  "Staff",
  "Suppliers",
  "Contractors",
  "Expenses",
  "FinanceCategories",
  "LabourTypes",
  "LabourPayments",
  "Vehicles",
  "VehicleAssignments",
  "SKUs",
  "StoreTransactions",
  "WorkProgressReports",
  "WorkProgressDropdowns",
  "EquipmentProjects",
];

/**
 * Route wrapper rendered at /dashboard/project/:projectId
 * Validates that the user is assigned to the project, then sets it as active.
 * All child routes (work progress, finance, etc.) are rendered via <Outlet />.
 */
const ProjectWorkspace = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const activeProjectId = useSelector(selectActiveProjectId);
  const userRole = useSelector(selectUserRole);
  const isSuperAdmin = userRole === "super-admin";
  const userProjectIds = useSelector((state) => state.auth.user?.projectIds ?? []);
  // Track previous projectId to detect actual switches (not just remounts)
  const prevProjectIdRef = useRef(null);

  const { data: myProjectsData, isLoading: myLoading } = useGetMyProjectsQuery(
    undefined,
    { skip: isSuperAdmin },
  );
  const { data: allProjectsData, isLoading: allLoading } = useGetAllProjectsForMatrixQuery();

  const isLoading = isSuperAdmin ? allLoading : (allLoading || myLoading);

  const allProjects = allProjectsData?.data ?? allProjectsData?.projects ?? [];

  const candidateProjects = isSuperAdmin
    ? allProjects
    : (() => {
        const fromApi = myProjectsData?.projects ?? [];
        if (fromApi.length > 0) return fromApi;
        return allProjects.filter((p) =>
          userProjectIds.includes(p._id?.toString() ?? p.id?.toString())
        );
      })();

  useEffect(() => {
    if (isLoading) return;

    const matched = candidateProjects.find(
      (p) => (p._id?.toString() ?? p.id?.toString()) === projectId,
    );

    if (!matched) {
      navigate("/dashboard", { replace: true });
      return;
    }

    if (activeProjectId !== projectId) {
      dispatch(
        setActiveProject({
          id: projectId,
          name: matched.projectName,
          city: matched.city,
        }),
      );
    }

    // When the active project genuinely changes, invalidate all project-scoped
    // cache so every child route fetches fresh data for the new project.
    if (prevProjectIdRef.current !== null && prevProjectIdRef.current !== projectId) {
      dispatch(appApi.util.invalidateTags(PROJECT_SCOPED_TAGS));
    }
    prevProjectIdRef.current = projectId;
  }, [projectId, candidateProjects, isLoading, activeProjectId, dispatch, navigate]);

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // key forces all child route components to remount on project switch,
  // clearing local state and triggering fresh RTK Query fetches.
  return <Outlet key={projectId} />;
};

export default ProjectWorkspace;
